import { useState, useCallback } from 'react';
import {
  makeStyles,
  Popover,
  Button,
  InputBase,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Radio,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SearchIcon from '@material-ui/icons/Search';
import { useSelector } from 'react-redux';

const useStyles = makeStyles(theme => ({
  // Основной контейнер селектора (как в AccountSelector)
  container: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: 'rgba(208, 208, 208, 0.13)',
    borderRadius: 10,
    transition: 'background-color .3s linear',
    maxWidth: '100%',
    '&:hover': {
      transition: 'background-color .3s linear',
      backgroundColor: 'rgba(208,208,208,0.24)',
    },
  },
  containerDisabled: {
    cursor: 'not-allowed',
    backgroundColor: 'rgba(0, 0, 0, 0.12)',
    opacity: 0.6,
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.12)',
    },
  },
  containerFlex: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
    flexDirection: 'row',
    padding: '0px 6px 0px 14px',
    width: 'calc(100% - 32px)',
  },
  titleGray: {
    fontWeight: 400,
    color: 'gray',
    fontSize: '12px',
    marginRight: '8px',
  },
  titleBlack: {
    color: 'black',
    fontSize: '14px',
    marginRight: '2px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  titleDisabled: {
    color: 'rgba(0, 0, 0, 0.38)',
  },
  chevron: {
    borderLeft: '1px solid rgb(218, 218, 218)',
    display: 'flex',
    height: '100%',
    padding: '8px',
  },
  chevronIcon: {
    width: 16,
    height: 16,
    fill: 'gray',
    color: 'gray',
    transform: 'rotate( 0deg )',
    transition: 'transform .3s linear',
  },

  // Стили попапа
  modal: {
    display: 'flex',
    flexDirection: 'column',
    width: 400,
    maxHeight: 500,
    overflow: 'hidden',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
  },

  // Поиск
  searchContainer: {
    padding: '12px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  searchInput: {
    fontSize: 14,
    padding: '8px 12px',
    border: '1px solid #e1e4e8',
    borderRadius: 8,
    width: '100%',
  },

  // Список инструментов
  listContainer: {
    flex: 1,
    overflow: 'auto',
    maxHeight: 300,
    minHeight: 200,
  },
  listItem: {
    padding: '6px 24px',
    borderBottom: '1px solid rgb(244, 244, 244)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  listItemSelected: {
    backgroundColor: 'rgba(158, 190, 222, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.16)',
    },
  },

  // Кнопки
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
  },
  button: {
    minWidth: 80,
  },

  // Загрузка
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },

  // Подсветка совпадений при поиске
  searchHighlight: {
    backgroundColor: '#fff3cd',
    borderRadius: 2,
    padding: '0 1px',
  },

  // Пустое состояние
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: '#666',
  },

  // Красная кнопка Clear
  clearButton: {
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
      borderColor: '#bd2130',
    },
  },
}));

const AssessmentSelector = ({ selectedInstrument = null, onInstrumentChange, assessmentInstruments, surveysStatus, hideAllOption = false, disabled = false }) => {
  const classes = useStyles();
  const {
    en: { btn },
  } = useSelector(state => state.i18n);

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [searchQuery, setSearchQuery] = useState('');

  const [tempSelectedInstrument, setTempSelectedInstrument] = useState(selectedInstrument);

  const highlightMatch = useCallback((text, query) => {
    if (!query) return text;
    const index = text.toLowerCase().indexOf(query.toLowerCase());
    if (index === -1) return text;
    return (
      <>
        {text.slice(0, index)}
        <span className={classes.searchHighlight}>{text.slice(index, index + query.length)}</span>
        {text.slice(index + query.length)}
      </>
    );
  }, [classes.searchHighlight]);

  const filteredInstruments = (assessmentInstruments || [])
    .filter(instrument => instrument.id !== 'all')
    .filter(instrument => instrument.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  const handleClick = event => {
    if (disabled) return;
    setAnchorEl(event.currentTarget);
    setTempSelectedInstrument(selectedInstrument);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempSelectedInstrument(selectedInstrument);
    setSearchQuery('');
  };

  const handleInstrumentSelect = instrument => {
    setTempSelectedInstrument(instrument);
    if (onInstrumentChange) {
      onInstrumentChange(instrument);
    }
    setAnchorEl(null);
    setSearchQuery('');
  };

  const handleClear = () => {
    setTempSelectedInstrument(hideAllOption ? null : 'all');
    if (onInstrumentChange) {
      onInstrumentChange(hideAllOption ? null : 'all');
    }
    setAnchorEl(null);
    setSearchQuery('');
  };

  const getDisplayName = () => {
    if ((!selectedInstrument || selectedInstrument === 'all') && !hideAllOption) {
      return 'All Assessments';
    }
    if (typeof selectedInstrument === 'string') {
      const found = (assessmentInstruments || []).find(inst => inst.id === selectedInstrument);
      return found ? found.name : hideAllOption ? null : 'All Assessments';
    }
    return selectedInstrument.name || hideAllOption ? null : 'All Assessments';
  };

  const isLoading = surveysStatus === 'loading';

  return (
    <>
      <div className={`${classes.container} ${disabled ? classes.containerDisabled : ''}`} onClick={handleClick}>
        <div className={classes.containerFlex}>
          <div className={`${classes.titleGray} ${disabled ? classes.titleDisabled : ''}`}>Assessment:</div>
          <div className={`${classes.titleBlack} ${disabled ? classes.titleDisabled : ''}`}>{getDisplayName()}</div>
        </div>
        <div className={classes.chevron}>
          <KeyboardArrowDownIcon className={classes.chevronIcon} />
        </div>
      </div>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.modal}>
          {/* Поиск */}
          <div className={classes.searchContainer}>
            <InputBase
              placeholder="Search assessments..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={classes.searchInput}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </div>

          {/* Список инструментов */}
          <div className={classes.listContainer}>
            {isLoading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={24} />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  Loading assessments...
                </Typography>
              </div>
            ) : filteredInstruments && filteredInstruments.length > 0 ? (
              <List>
                {filteredInstruments.map(instrument => {
                  const isSelected =
                    (typeof selectedInstrument === 'string' &&
                      selectedInstrument === instrument.id) ||
                    selectedInstrument?.id === instrument.id;

                  return (
                    <ListItem
                      key={instrument.id}
                      className={`${classes.listItem} ${
                        isSelected ? classes.listItemSelected : ''
                      }`}
                      onClick={() => handleInstrumentSelect(instrument)}
                    >
                      <Radio
                        checked={isSelected}
                        size="small"
                        color="primary"
                        style={{
                          marginLeft: -12,
                        }}
                      />
                      <ListItemText primary={highlightMatch(instrument.name, searchQuery)} />
                    </ListItem>
                  );
                })}
              </List>
            ) : (
              <div className={classes.emptyState}>
                <Typography variant="body2">
                  {searchQuery ? 'No assessments found' : 'No assessments available'}
                </Typography>
              </div>
            )}
          </div>

          {/* Кнопки */}
          <div className={classes.buttonsContainer}>
            <Button onClick={handleClose} className={classes.button}>
              {btn.cancel || 'Cancel'}
            </Button>
            {!hideAllOption && <Button
              variant="contained"
              onClick={handleClear}
              className={`${classes.button} ${classes.clearButton}`}
            >
              Clear
            </Button>}
          </div>
        </div>
      </Popover>
    </>
  );
};

export default AssessmentSelector;
